import { patchState, signalStore, type, withMethods, withState } from "@ngrx/signals";
import { IDialogItem } from '../entities/dialog.interface';
import { withEntities, entityConfig } from "@ngrx/signals/entities";
import { v4 as uuidv4 } from 'uuid';
import { effect, inject, Injector, Signal } from "@angular/core";
import { Subject, Observable } from 'rxjs';

export interface DialogState {
  activeDialog: IDialogItem | null;
}

/**
 * Options for {@link DialogStore.discardUnsavedChanges}.
 *
 * Without any option the dialog keeps its historical behaviour: it only reports
 * the answer through the returned observable (`true` = discard, `false` = save)
 * and closes itself immediately. Pass `onSave` when the caller can actually
 * persist the form from the dialog.
 */
export type TDiscardUnsavedChangesOptions = {
  /** Overrides the default `GUARDS.CLOSING_UNSAVED.TITLE` key. */
  title?: string;
  /** Overrides the default `GUARDS.CLOSING_UNSAVED.MESSAGE` key. */
  message?: string;
  /** Runs when the user chooses to discard, right before the dialog closes. */
  onDiscard?: () => void;
  /**
   * Runs when the user chooses to save. Return `false` when the submit was
   * rejected locally (invalid form) so the dialog closes right away and lets the
   * form surface its own validation errors. Any other return value keeps the
   * dialog open — and spinning, if `loadingOnsignal` is set — until one of the
   * `closeOnSignal` signals settles the request.
   */
  onSave?: () => boolean | void;
  /** Signals that settle the save (both the `*Loaded` and the `*Error` ones). */
  closeOnSignal?: IDialogItem['closeOnSignal'];
  /** Keeps the save button in loading state while the request is in flight. */
  loadingOnsignal?: Signal<boolean>;
};

export const initialDialogState: DialogState = {
  activeDialog: null
};

const dialogEntityConfig = entityConfig({
  entity: type<IDialogItem>(),
  collection: 'dialogCollection',
  selectId: (dialog: IDialogItem) => dialog.id,
});

/**
 * Store for managing dialogs in the application
 * Allows creating, removing, and controlling the loading state of dialogs
 */
export const DialogStore = signalStore(
  { providedIn: 'root' },
  withEntities(dialogEntityConfig),
  withState<DialogState>(initialDialogState),
  withMethods((store, injector = inject(Injector)) => {
    /**
     * Removes the current active dialog
     */
    const closeDialog = () => patchState(store, { activeDialog: null });

    /**
     * Creates a new dialog and sets it as active
     * @param dialogConfig Dialog configuration
     * @returns The created dialog with helper methods
     */
    const openDialog = (dialogConfig: IDialogItem): IDialogItem =>  {
      const dialog: IDialogItem = {
        ...dialogConfig,
        id: uuidv4(),
      };

      patchState(store, { activeDialog: dialog });
      dialogConfig.closeOnSignal?.forEach((signal) => {
        let initialValue = signal();
        const effectRef = effect(() => {
          const currentValue = signal();
          if (initialValue) {
            initialValue = currentValue;
            return;
          }
          if (currentValue) {
            closeDialog();
            effectRef.destroy();
          }
        }, { injector });
      })

      return dialog;
    }

    /**
     * Opens the standard "you have unsaved changes" dialog.
     *
     * @param options See {@link TDiscardUnsavedChangesOptions}.
     * @returns An observable that emits `true` when the user discards and
     * `false` when the user chooses to save.
     */
    const discardUnsavedChanges = (options?: TDiscardUnsavedChangesOptions): Observable<boolean> => {
      const result$ = new Subject<boolean>();
      const settle = (discarded: boolean) => {
        result$.next(discarded);
        result$.complete();
      };

      openDialog({
        title: options?.title ?? 'GUARDS.CLOSING_UNSAVED.TITLE',
        message: options?.message ?? 'GUARDS.CLOSING_UNSAVED.MESSAGE',
        closeOnSignal: options?.closeOnSignal,
        primaryButton: {
          label: 'GUARDS.CLOSING_UNSAVED.IGNORE',
          action: () => {
            settle(true);
            options?.onDiscard?.();
            closeDialog();
          }
        },
        secondaryButton: {
          label: 'GUARDS.CLOSING_UNSAVED.SAVE',
          loadingOnsignal: options?.loadingOnsignal,
          action: () => {
            settle(false);

            if (!options?.onSave) {
              closeDialog();
              return;
            }

            const submitted = options.onSave();
            /**
             * Rejected before reaching the API (invalid form): close so the user
             * sees the validation errors. Otherwise `closeOnSignal` closes the
             * dialog once the request finishes, success or failure.
             */
            if (submitted === false || !options.closeOnSignal?.length) {
              closeDialog();
            }
          }
        }
      });
      return result$.asObservable();
    }

    return {
      closeDialog,
      openDialog,
      discardUnsavedChanges,

      /**
       * Checks if there is an active dialog
       * @returns true if there is an active dialog
       */
      hasActiveDialog(): boolean {
        return !!store.activeDialog();
      }
    };
  }),
);